import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../../utils/api';
import { 
  Plus, Trash2, X, Save, Send, FileText, Eye,
  Sparkles, Star, Search, Phone, ChevronDown, Check, 
  AlertTriangle, Loader2, User, Edit2, Lightbulb, CheckCircle2,
  Globe, ExternalLink
} from 'lucide-react';

// Helpers
const formatCurrency = (amount) => {
  if (!amount && amount !== 0) return '';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(amount);
};

const calculateMargin = (cost, price) => {
  if (!price || price === 0) return 0;
  return ((price - cost) / price * 100).toFixed(1);
};

const formatDate = (dateString) => {
  if (!dateString) return '';
  return new Date(dateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

// ============================================================================
// MODALS
// ============================================================================

function NewClientModal({ onSave, onClose }) {
  const [name, setName] = useState('');
  const [contactName, setContactName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [billingAddress, setBillingAddress] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const client = await api.post('/clients', { name, contactName, email, phone, billingAddress, category: 'PRIVATE' });
      onSave(client);
    } catch (err) { alert(err.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md animate-slide-up">
        <div className="p-4 border-b flex items-center justify-between">
          <h2 className="text-lg font-semibold">New Client</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="flex gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
              <p className="text-xs text-amber-700">Client name must match <strong>exactly</strong> as it appears in QuickBooks.</p>
            </div>
          </div>
          <div>
            <label className="label">Company Name *</label>
            <input value={name} onChange={e => setName(e.target.value)} className="input" required />
          </div>
          <div>
            <label className="label">Contact Name</label>
            <input value={contactName} onChange={e => setContactName(e.target.value)} className="input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Email</label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} className="input" />
            </div>
            <div>
              <label className="label">Phone</label>
              <input value={phone} onChange={e => setPhone(e.target.value)} className="input" />
            </div>
          </div>
          <div>
            <label className="label">Billing Address</label>
            <textarea value={billingAddress} onChange={e => setBillingAddress(e.target.value)} className="input" rows={2} />
          </div>
          <div className="flex gap-3 pt-2">
            <button type="submit" className="btn-primary flex-1" disabled={saving}>{saving ? 'Creating...' : 'Create Client'}</button>
            <button type="button" onClick={onClose} className="btn-secondary flex-1">Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function NewVendorModal({ onSave, onClose, defaultMarket, defaultName }) {
  const [name, setName] = useState(defaultName || '');
  const [category, setCategory] = useState('');
  const [contactName, setContactName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [market, setMarket] = useState(defaultMarket || 'Las Vegas');
  const [saving, setSaving] = useState(false);

  const categories = ['DJ', 'Band', 'Musician', 'Production', 'Lighting', 'Sound', 'Photography', 'Videography', 'Catering', 'Rentals', 'Decor', 'Transportation', 'Security', 'Staffing', 'Other'];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const vendor = await api.post('/vendors', { name, category, contactName, email, phone, market, status: 'ACTIVE' });
      onSave(vendor);
    } catch (err) { alert(err.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md animate-slide-up">
        <div className="p-4 border-b flex items-center justify-between">
          <h2 className="text-lg font-semibold">Add New Vendor</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div>
            <label className="label">Vendor Name *</label>
            <input value={name} onChange={e => setName(e.target.value)} className="input" required />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Category *</label>
              <select value={category} onChange={e => setCategory(e.target.value)} className="input" required>
                <option value="">Select...</option>
                {categories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
              </select>
            </div>
            <div>
              <label className="label">Market</label>
              <input value={market} onChange={e => setMarket(e.target.value)} className="input" placeholder="Las Vegas" />
            </div>
          </div>
          <div>
            <label className="label">Contact Name</label>
            <input value={contactName} onChange={e => setContactName(e.target.value)} className="input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Email</label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} className="input" />
            </div>
            <div>
              <label className="label">Phone</label>
              <input value={phone} onChange={e => setPhone(e.target.value)} className="input" />
            </div>
          </div>
          <div className="flex gap-3 pt-2">
            <button type="submit" className="btn-primary flex-1" disabled={saving}>{saving ? 'Adding...' : 'Add Vendor'}</button>
            <button type="button" onClick={onClose} className="btn-secondary flex-1">Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function SendWarningModal({ unconfirmedItems, onSendAnyway, onGoBack }) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md animate-slide-up">
        <div className="p-4 border-b bg-amber-50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-amber-900">Review Before Sending</h2>
              <p className="text-sm text-amber-700">Some line items need attention</p>
            </div>
          </div>
        </div>
        <div className="p-4 space-y-4">
          <p className="text-gray-700">The following line items are not confirmed:</p>
          <ul className="text-sm text-gray-600 list-disc list-inside space-y-1">
            {unconfirmedItems.map((item, idx) => <li key={idx}>{item.description || `Line item ${idx + 1}`}</li>)}
          </ul>
          <div className="flex gap-3 pt-2">
            <button onClick={onGoBack} className="btn-secondary flex-1">Go Back & Fix</button>
            <button onClick={onSendAnyway} className="btn-primary flex-1">Send Anyway</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// VENDOR SELECTOR - Autocomplete with search
// ============================================================================

function VendorSelector({ value, onChange, vendors, onAddNew, onFindMatch, onDiscover }) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');

  const selectedVendor = vendors.find(v => v.id === value);

  let filteredVendors = vendors;
  if (search) {
    const q = search.toLowerCase();
    filteredVendors = vendors.filter(v => v.name.toLowerCase().includes(q) || v.contactName?.toLowerCase().includes(q));
  }
  filteredVendors = [...filteredVendors].sort((a, b) => {
    if (a.isPreferred && !b.isPreferred) return -1;
    if (!a.isPreferred && b.isPreferred) return 1;
    return a.name.localeCompare(b.name);
  }).slice(0, 50);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && search && filteredVendors.length === 0) {
      e.preventDefault();
      setIsOpen(false);
      onAddNew(search);
    }
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`input w-full text-left flex items-center justify-between text-sm ${!selectedVendor ? 'text-gray-400' : ''}`}
      >
        <span className="flex items-center gap-2 truncate">
          {selectedVendor ? (
            <>{selectedVendor.isPreferred && <Star className="w-3 h-3 fill-amber-400 text-amber-500" />}{selectedVendor.name}</>
          ) : 'Select vendor...'}
        </span>
        <ChevronDown className="w-4 h-4 text-gray-400" />
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
          <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border rounded-lg shadow-lg">
            {/* Search */}
            <div className="p-2 border-b">
              <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Search vendors..."
                  className="w-full pl-8 pr-3 py-1.5 text-sm border rounded focus:outline-none focus:ring-2 focus:ring-hub-500"
                  autoFocus
                />
              </div>
            </div>

            {/* Vendor List */}
            <div className="max-h-48 overflow-y-auto">
              {filteredVendors.length === 0 ? (
                <div className="p-3 text-center">
                  <p className="text-sm text-gray-500">No vendors found</p>
                  {search && (
                    <button
                      type="button"
                      onClick={() => { setIsOpen(false); onAddNew(search); }}
                      className="mt-2 px-3 py-1.5 text-sm font-medium text-hub-600 hover:bg-hub-50 rounded flex items-center justify-center gap-1 mx-auto"
                    >
                      <Plus className="w-4 h-4" /> Add "{search}"
                    </button>
                  )}
                </div>
              ) : (
                filteredVendors.map(vendor => (
                  <button
                    key={vendor.id}
                    type="button"
                    onClick={() => { onChange(vendor.id); setIsOpen(false); setSearch(''); }}
                    className={`w-full px-3 py-2 text-left hover:bg-gray-50 flex items-center gap-2 ${value === vendor.id ? 'bg-hub-50' : ''}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1">
                        {vendor.isPreferred && <Star className="w-3 h-3 fill-amber-400 text-amber-500 shrink-0" />}
                        <span className="font-medium text-sm truncate">{vendor.name}</span>
                      </div>
                      {vendor.contactName && <p className="text-xs text-gray-500 truncate">{vendor.contactName}</p>}
                    </div>
                    {value === vendor.id && <Check className="w-4 h-4 text-hub-600 shrink-0" />}
                  </button>
                ))
              )}
            </div>

            {/* Actions */}
            <div className="border-t p-2 flex gap-2">
              <button
                type="button"
                onClick={() => { setIsOpen(false); onFindMatch(); }}
                className="flex-1 px-3 py-1.5 text-xs font-medium text-hub-600 hover:bg-hub-50 rounded flex items-center justify-center gap-1"
              >
                <Sparkles className="w-3 h-3" /> Find Match
              </button>
              <button
                type="button"
                onClick={() => { setIsOpen(false); onDiscover(); }}
                className="flex-1 px-3 py-1.5 text-xs font-medium text-purple-600 hover:bg-purple-50 rounded flex items-center justify-center gap-1"
              >
                <Search className="w-3 h-3" /> Discover
              </button>
              <button
                type="button"
                onClick={() => { setIsOpen(false); onAddNew(''); }}
                className="flex-1 px-3 py-1.5 text-xs font-medium text-hub-600 hover:bg-hub-50 rounded flex items-center justify-center gap-1"
              >
                <Plus className="w-3 h-3" /> New
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ============================================================================
// VENDOR SUGGESTIONS (AI search results)
// ============================================================================

function VendorSuggestions({ suggestions, loading, onSelect, onDismiss }) {
  if (loading) {
    return (
      <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center gap-2 text-blue-700">
        <Loader2 className="w-4 h-4 animate-spin" />
        <span className="text-sm">Searching vendors in your network...</span>
      </div>
    );
  }

  if (!suggestions || suggestions.length === 0) return null;

  return (
    <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 text-blue-700">
          <Sparkles className="w-4 h-4" />
          <span className="text-sm font-medium">Found {suggestions.length} matching vendors</span>
        </div>
        <button onClick={onDismiss} className="text-blue-500 hover:text-blue-700"><X className="w-4 h-4" /></button>
      </div>
      <div className="space-y-2">
        {suggestions.map((vendor, idx) => (
          <div key={idx} className="bg-white p-2 rounded border flex items-center justify-between gap-2">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1">
                {vendor.isPreferred && <Star className="w-3 h-3 fill-amber-400 text-amber-500" />}
                <span className="font-medium text-sm">{vendor.name}</span>
              </div>
              <div className="flex gap-3 text-xs text-gray-500">
                {vendor.contactName && <span>{vendor.contactName}</span>}
                {vendor.phone && <span>{vendor.phone}</span>}
              </div>
            </div>
            <button onClick={() => onSelect(vendor)} className="px-2 py-1 bg-hub-600 text-white text-xs font-medium rounded hover:bg-hub-700">
              Select
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================================================================
// LINE ITEM ROW
// ============================================================================

function LineItemRow({ item, index, categories, vendors, onChange, onDelete, onSearchVendors, onAddVendor, onDiscover, vendorSearch }) {
  const margin = calculateMargin(item.cost || 0, item.price || 0);
  const marginColor = margin >= 25 ? 'text-green-600' : margin >= 15 ? 'text-amber-600' : 'text-red-600';

  return (
    <div className="border rounded-lg p-4 space-y-3">
      {/* Row 1: Category, Description, Confirmed */}
      <div className="flex gap-3">
        <div className="w-40">
          <label className="label text-xs">Category</label>
          <select value={item.categoryId || ''} onChange={(e) => onChange(index, 'categoryId', e.target.value)} className="input text-sm">
            <option value="">Select...</option>
            {categories.map(cat => <option key={cat.id} value={cat.id}>{cat.label || cat.name}</option>)}
          </select>
        </div>
        <div className="flex-1">
          <label className="label text-xs">Description</label>
          <input type="text" value={item.description || ''} onChange={(e) => onChange(index, 'description', e.target.value)} className="input text-sm" placeholder="Line item description..." />
        </div>
        <div className="w-24 flex items-end justify-center pb-2">
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={item.confirmed || false} onChange={(e) => onChange(index, 'confirmed', e.target.checked)} className="w-4 h-4 rounded text-hub-600" />
            <span className="text-xs text-gray-600">Confirmed</span>
          </label>
        </div>
      </div>

      {/* Row 2: Vendor, Cost, Price, Margin, Delete */}
      <div className="flex gap-3 items-end">
        <div className="flex-1">
          <label className="label text-xs">Vendor</label>
          <VendorSelector
            value={item.vendorId}
            onChange={(id) => onChange(index, 'vendorId', id)}
            vendors={vendors}
            onAddNew={(name) => onAddVendor(index, name)}
            onFindMatch={() => onSearchVendors(index, item.description)}
            onDiscover={() => onDiscover(index, item.description)}
          />
        </div>
        <div className="w-28">
          <label className="label text-xs">Cost</label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
            <input type="number" value={item.cost || ''} onChange={(e) => onChange(index, 'cost', parseFloat(e.target.value) || 0)} className="input text-sm pl-7" placeholder="0" min="0" />
          </div>
        </div>
        <div className="w-28">
          <label className="label text-xs">Price</label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
            <input type="number" value={item.price || ''} onChange={(e) => onChange(index, 'price', parseFloat(e.target.value) || 0)} className="input text-sm pl-7" placeholder="0" min="0" />
          </div>
        </div>
        <div className="w-20 text-center pb-2">
          <p className="text-xs text-gray-500">Margin</p>
          <p className={`text-sm font-semibold ${marginColor}`}>{margin}%</p>
        </div>
        <button onClick={() => onDelete(index)} className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg mb-1">
          <Trash2 className="w-4 h-4" />
        </button>
      </div>

      {/* Vendor AI Suggestions */}
      {vendorSearch && (
        <VendorSuggestions
          suggestions={vendorSearch.results}
          loading={vendorSearch.loading}
          onSelect={(vendor) => {
            if (vendor.id) {
              onChange(index, 'vendorId', vendor.id);
            }
            onSearchVendors(index, null); // dismiss
          }}
          onDismiss={() => onSearchVendors(index, null)}
        />
      )}
    </div>
  );
}

// ============================================================================
// AI SUGGESTIONS PANEL
// ============================================================================

function AISuggestions({ suggestions, onAddToQuote, onDelete, onDismissAll }) {
  if (!suggestions || suggestions.length === 0) return null;

  return (
    <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 text-amber-700">
          <Lightbulb className="w-4 h-4" />
          <span className="text-sm font-medium">AI Suggestions ({suggestions.length})</span>
        </div>
        <button onClick={onDismissAll} className="text-xs text-amber-600 hover:text-amber-800">Dismiss All</button>
      </div>
      <div className="space-y-2">
        {suggestions.map((sug, idx) => (
          <div key={sug.id || idx} className="flex items-center justify-between gap-3 bg-white p-2 rounded border border-amber-100">
            <span className="text-sm text-amber-900 flex-1">{sug.text || sug}</span>
            <div className="flex gap-1 shrink-0">
              <button onClick={() => onAddToQuote(sug)} className="px-2 py-1 text-xs font-medium text-green-700 hover:bg-green-50 rounded" title="Add as line item">
                <Plus className="w-3 h-3" />
              </button>
              <button onClick={() => onDelete(sug)} className="px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50 rounded" title="Delete">
                <X className="w-3 h-3" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================================================================
// VENDOR GAPS ALERT (missing vendors)
// ============================================================================

function VendorGapsAlert({ gaps, onDiscover, onDismiss }) {
  if (!gaps || gaps.length === 0) return null;

  return (
    <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 text-purple-700">
          <AlertTriangle className="w-4 h-4" />
          <span className="text-sm font-medium">Missing Vendors ({gaps.length})</span>
        </div>
        <button onClick={onDismiss} className="text-xs text-purple-600 hover:text-purple-800">Dismiss</button>
      </div>
      <p className="text-xs text-purple-600 mb-3">No vendors found in your database for these services. Use AI Discovery to find vendors.</p>
      <div className="space-y-2">
        {gaps.map((gap, idx) => (
          <div key={idx} className="flex items-center justify-between gap-3 bg-white p-2 rounded border border-purple-100">
            <div className="flex-1 min-w-0">
              <span className="text-sm font-medium text-purple-900">{gap.categoryName || gap.description}</span>
              {gap.description && gap.categoryName && (
                <p className="text-xs text-purple-600 truncate">{gap.description}</p>
              )}
            </div>
            <button 
              onClick={() => onDiscover(gap)} 
              className="px-3 py-1.5 text-xs font-medium bg-purple-600 text-white rounded hover:bg-purple-700 flex items-center gap-1 shrink-0"
            >
              <Search className="w-3 h-3" />
              Discover
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================================================================
// VENDOR DISCOVERY MODAL
// ============================================================================

function VendorDiscoveryModal({ isOpen, onClose, initialQuery, initialMarket, categories, onVendorAdded }) {
  const [query, setQuery] = useState(initialQuery || '');
  const [market, setMarket] = useState(initialMarket || 'Las Vegas');
  const [category, setCategory] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [addedNames, setAddedNames] = useState(new Set());
  const [showAddForm, setShowAddForm] = useState(null);

  // Reset when modal opens with new params
  useEffect(() => {
    if (isOpen) {
      setQuery(initialQuery || '');
      setMarket(initialMarket || 'Las Vegas');
      setResults([]);
      setError('');
    }
  }, [isOpen, initialQuery, initialMarket]);

  const handleSearch = async () => {
    if (!query.trim()) {
      setError('Enter a search term');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const res = await api.post('/ai/discover-vendors', {
        query: query.trim(),
        market,
        category: category || undefined
      });
      
      setResults(res.results || []);
      
      if (res.results?.length === 0) {
        setError('No vendors found. Try different search terms.');
      }
    } catch (err) {
      console.error('Discovery failed:', err);
      setError(err.message || 'Search failed. Please try again.');
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddVendor = async (result) => {
    try {
      const vendor = await api.post('/vendors', {
        name: result.name,
        category: category || result.category || 'Other',
        phone: result.phone || '',
        website: result.website || '',
        address: result.address || '',
        market: market,
        notes: result.description || '',
        status: 'ACTIVE'
      });
      
      setAddedNames(prev => new Set([...prev, result.name]));
      setShowAddForm(null);
      
      if (onVendorAdded) {
        onVendorAdded(vendor);
      }
    } catch (err) {
      alert('Failed to add vendor: ' + err.message);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col animate-slide-up">
        {/* Header */}
        <div className="p-4 border-b flex items-center justify-between shrink-0">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-purple-600" />
            <h2 className="text-lg font-semibold">AI Vendor Discovery</h2>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded"><X className="w-5 h-5" /></button>
        </div>

        {/* Search Form */}
        <div className="p-4 border-b shrink-0">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="md:col-span-2">
              <label className="label text-xs">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={query}
                  onChange={e => setQuery(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleSearch()}
                  placeholder="fireworks, balloon artist, tent rental..."
                  className="input pl-10 text-sm"
                  autoFocus
                />
              </div>
            </div>
            <div>
              <label className="label text-xs">Market</label>
              <input
                type="text"
                value={market}
                onChange={e => setMarket(e.target.value)}
                className="input text-sm"
                placeholder="Las Vegas"
              />
            </div>
          </div>
          <div className="mt-3 flex items-center justify-between">
            <p className="text-xs text-gray-500">AI searches the web to find vendors in your market</p>
            <button onClick={handleSearch} disabled={loading} className="btn-primary text-sm">
              {loading ? <><Loader2 className="w-4 h-4 animate-spin" />Searching...</> : <><Search className="w-4 h-4" />Search</>}
            </button>
          </div>
          {loading && (
            <p className="text-xs text-purple-600 mt-2">This may take 2-3 minutes...</p>
          )}
        </div>

        {/* Results */}
        <div className="flex-1 overflow-y-auto p-4">
          {error && (
            <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm mb-4">{error}</div>
          )}

          {results.length > 0 && (
            <div className="space-y-3">
              <p className="text-sm text-gray-600">Found {results.length} vendors</p>
              {results.map((result, idx) => {
                const isAdded = addedNames.has(result.name);
                return (
                  <div key={idx} className="border rounded-lg p-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-sm">{result.name}</h4>
                        {result.description && (
                          <p className="text-xs text-gray-600 mt-1 line-clamp-2">{result.description}</p>
                        )}
                        <div className="flex flex-wrap gap-3 mt-2 text-xs text-gray-500">
                          {result.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{result.phone}</span>}
                          {result.address && <span className="truncate max-w-[200px]">{result.address}</span>}
                        </div>
                      </div>
                      <div className="shrink-0">
                        {isAdded ? (
                          <span className="flex items-center gap-1 text-green-600 text-xs"><Check className="w-4 h-4" />Added</span>
                        ) : (
                          <button 
                            onClick={() => handleAddVendor(result)} 
                            className="px-3 py-1.5 text-xs font-medium bg-hub-600 text-white rounded hover:bg-hub-700 flex items-center gap-1"
                          >
                            <Plus className="w-3 h-3" />Add
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {!loading && results.length === 0 && !error && (
            <div className="text-center py-8 text-gray-500">
              <Search className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>Enter a search term to discover vendors</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t shrink-0">
          <button onClick={onClose} className="btn-secondary w-full">Done</button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// RFQ DISPLAY (shown after commit)
// ============================================================================

function RFQDisplay({ rfpText, parsedAt, onEdit }) {
  const [collapsed, setCollapsed] = useState(true);
  
  return (
    <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FileText className="w-4 h-4 text-gray-500" />
          <span className="text-sm font-medium text-gray-700">RFQ{parsedAt ? `, Parsed ${formatDate(parsedAt)}` : ''}</span>
        </div>
        <div className="flex gap-3">
          <button onClick={onEdit} className="text-xs text-hub-600 hover:text-hub-700 flex items-center gap-1">
            <Edit2 className="w-3 h-3" /> Edit & Re-parse
          </button>
          <button onClick={() => setCollapsed(!collapsed)} className="text-xs text-gray-500 hover:text-gray-700">
            {collapsed ? 'Show' : 'Hide'}
          </button>
        </div>
      </div>
      {!collapsed && (
        <div className="mt-3 text-sm text-gray-600 whitespace-pre-wrap bg-white p-3 rounded border max-h-40 overflow-y-auto">
          {rfpText}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// COMMIT BANNER (shown after parse, before commit)
// ============================================================================

function CommitBanner({ onCommit, onClear }) {
  return (
    <div className="p-3 bg-green-50 border border-green-200 rounded-lg flex items-center justify-between">
      <div className="flex items-center gap-2 text-green-700">
        <CheckCircle2 className="w-5 h-5" />
        <span className="font-medium">RFQ Parsed — Review line items below, then commit or clear</span>
      </div>
      <div className="flex gap-2">
        <button onClick={onClear} className="px-3 py-1.5 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded">Clear All</button>
        <button onClick={onCommit} className="px-3 py-1.5 text-sm font-medium bg-green-600 text-white rounded hover:bg-green-700">Commit RFQ</button>
      </div>
    </div>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function EventBuilder() {
  const { eventId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [categories, setCategories] = useState([]);

  const [event, setEvent] = useState({
    name: '', clientId: '', date: '', location: '', guestCount: '', notes: '',
    rfpText: '', rfpParsedAt: null, status: 'INQUIRY'
  });

  const [lineItems, setLineItems] = useState([]);
  const [aiSuggestions, setAiSuggestions] = useState([]);
  const [vendorGaps, setVendorGaps] = useState([]);
  
  // Brainstorm state
  const [brainstormText, setBrainstormText] = useState('');
  const [brainstormLoading, setBrainstormLoading] = useState(false);
  const [pendingCommit, setPendingCommit] = useState(false); // true after parse, before commit
  
  // Vendor searches per line item
  const [vendorSearches, setVendorSearches] = useState({});
  
  // Modals
  const [showClientModal, setShowClientModal] = useState(false);
  const [showVendorModal, setShowVendorModal] = useState(null);
  const [showSendWarning, setShowSendWarning] = useState(false);
  const [showDiscoveryModal, setShowDiscoveryModal] = useState(null); // { query, market }
  
  const [saving, setSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  // Warn before leaving with unsaved changes
  useEffect(() => {
    const handler = (e) => { if (isDirty) { e.preventDefault(); e.returnValue = ''; } };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  // Load data
  useEffect(() => {
    const load = async () => {
      try {
        const [clientsData, vendorsData, categoriesData] = await Promise.all([
          api.get('/clients'), api.get('/vendors'), api.get('/event-categories')
        ]);
        setClients(Array.isArray(clientsData) ? clientsData : clientsData?.clients || []);
        setVendors(Array.isArray(vendorsData) ? vendorsData : vendorsData?.vendors || []);
        setCategories((Array.isArray(categoriesData) ? categoriesData : categoriesData?.categories || []).filter(c => c.active));

        if (eventId) {
          const data = await api.get(`/events/${eventId}`);
          setEvent({
            name: data.name || '', clientId: data.clientId || '',
            date: data.date ? data.date.split('T')[0] : '', location: data.location || '',
            guestCount: data.guestCount != null ? String(data.guestCount) : '',
            notes: data.notes || '', rfpText: data.rfpText || '',
            rfpParsedAt: data.rfpParsedAt || null, status: data.status || 'INQUIRY'
          });
          if (data.aiSuggestions) {
            try {
              setAiSuggestions(typeof data.aiSuggestions === 'string' ? JSON.parse(data.aiSuggestions) : data.aiSuggestions);
            } catch {}
          }
          if (data.quotes?.[0]?.lineItems) {
            setLineItems(data.quotes[0].lineItems.map(li => ({
              id: li.id, categoryId: li.categoryId || '', description: li.description || '',
              vendorId: li.vendorId || '', cost: parseFloat(li.cost) || 0, price: parseFloat(li.price) || 0, confirmed: li.confirmed || false
            })));
          }
        }
      } catch (err) { console.error('Load failed:', err); }
      finally { setLoading(false); }
    };
    load();
  }, [eventId]);

  // Totals
  const totalCost = lineItems.reduce((sum, li) => sum + (li.cost || 0), 0);
  const totalPrice = lineItems.reduce((sum, li) => sum + (li.price || 0), 0);
  const totalMargin = calculateMargin(totalCost, totalPrice);
  const profit = totalPrice - totalCost;

  // Handlers
  const handleEventChange = (field, value) => { setEvent(prev => ({ ...prev, [field]: value })); setIsDirty(true); };
  
  const handleLineItemChange = (index, field, value) => {
    setLineItems(prev => { const u = [...prev]; u[index] = { ...u[index], [field]: value }; return u; });
    setIsDirty(true);
  };
  
  const addLineItem = () => {
    setLineItems(prev => [...prev, { categoryId: '', description: '', vendorId: '', cost: 0, price: 0, confirmed: false }]);
    setIsDirty(true);
  };
  
  const deleteLineItem = (index) => {
    setLineItems(prev => prev.filter((_, i) => i !== index));
    setIsDirty(true);
  };

  // ==================== BRAINSTORM / PARSE ====================
  
  const handleBrainstorm = async () => {
    if (!brainstormText.trim()) return;
    setBrainstormLoading(true);
    
    try {
      const result = await api.post('/brainstorm/parse', { text: brainstormText, market: event.location || 'Las Vegas' });
      
      // Populate line items immediately
      if (result.lineItems?.length > 0) {
        const newItems = result.lineItems.map(item => ({
          categoryId: item.categoryId || '',
          description: item.description || '',
          vendorId: item.suggestedVendorId || '',
          cost: item.estimatedCost || 0,
          price: item.estimatedPrice || 0,
          confirmed: false
        }));
        
        if (lineItems.length > 0 && lineItems.some(li => li.description)) {
          if (confirm('Replace existing line items with parsed results?')) {
            setLineItems(newItems);
          } else {
            setLineItems(prev => [...prev, ...newItems]);
          }
        } else {
          setLineItems(newItems);
        }
      }
      
      // Populate suggestions
      if (result.suggestions?.length > 0) {
        setAiSuggestions(result.suggestions.map((s, i) => ({
          id: `sug-${Date.now()}-${i}`,
          text: typeof s === 'string' ? s : s.text
        })));
      }
      
      // Store vendor gaps for discovery prompts
      if (result.vendorGaps?.length > 0) {
        setVendorGaps(result.vendorGaps);
      } else {
        setVendorGaps([]);
      }
      
      // Populate event details (only if empty)
      if (result.eventDetails) {
        if (result.eventDetails.date && !event.date) handleEventChange('date', result.eventDetails.date);
        if (result.eventDetails.guestCount && !event.guestCount) handleEventChange('guestCount', String(result.eventDetails.guestCount));
        if (result.eventDetails.location && !event.location) handleEventChange('location', result.eventDetails.location);
      }
      
      // Show commit banner
      setPendingCommit(true);
      setIsDirty(true);
      
    } catch (err) {
      alert('Parse failed: ' + err.message);
    } finally {
      setBrainstormLoading(false);
    }
  };
  
  const commitRFQ = () => {
    handleEventChange('rfpText', brainstormText);
    handleEventChange('rfpParsedAt', new Date().toISOString());
    setBrainstormText('');
    setPendingCommit(false);
  };
  
  const clearParse = () => {
    setLineItems([]);
    setAiSuggestions([]);
    setBrainstormText('');
    setPendingCommit(false);
  };
  
  const editRFQ = () => {
    // Just bring back the textarea with original text - don't clear anything
    setBrainstormText(event.rfpText);
    handleEventChange('rfpText', '');
    handleEventChange('rfpParsedAt', null);
    // Line items and suggestions stay intact
  };

  // ==================== VENDOR SEARCH ====================
  
  const searchVendorsForLine = async (index, description) => {
    if (description === null) {
      // Dismiss
      setVendorSearches(prev => { const u = { ...prev }; delete u[index]; return u; });
      return;
    }
    
    setVendorSearches(prev => ({ ...prev, [index]: { loading: true, results: [] } }));
    try {
      const result = await api.post('/brainstorm/search-vendors', { query: description, market: event.location || 'Las Vegas' });
      setVendorSearches(prev => ({ ...prev, [index]: { loading: false, results: result.vendors || [] } }));
    } catch {
      setVendorSearches(prev => ({ ...prev, [index]: { loading: false, results: [] } }));
    }
  };

  const discoverVendors = (index, description) => {
    const cat = lineItems[index]?.categoryId ? categories.find(c => c.id === lineItems[index].categoryId)?.name : '';
    const query = description || cat || 'vendor';
    setShowDiscoveryModal({
      query,
      market: event.location || 'Las Vegas'
    });
  };

  // ==================== SUGGESTIONS ====================
  
  const addSuggestionToQuote = (sug) => {
    setLineItems(prev => [...prev, { categoryId: '', description: sug.text || sug, vendorId: '', cost: 0, price: 0, confirmed: false }]);
    setAiSuggestions(prev => prev.filter(s => s.id !== sug.id));
    setIsDirty(true);
  };
  
  const deleteSuggestion = (sug) => {
    setAiSuggestions(prev => prev.filter(s => s.id !== sug.id));
    setIsDirty(true);
  };

  // ==================== CLIENT/VENDOR CREATED ====================
  
  const handleClientCreated = (client) => {
    setClients(prev => [...prev, client]);
    handleEventChange('clientId', client.id);
    setShowClientModal(false);
  };
  
  const handleVendorCreated = (vendor) => {
    setVendors(prev => [...prev, vendor]);
    if (showVendorModal?.index !== undefined) {
      handleLineItemChange(showVendorModal.index, 'vendorId', vendor.id);
    }
    setShowVendorModal(null);
  };

  // ==================== SAVE ====================
  
  const handleSave = async () => {
    if (!event.name) { alert('Event name is required'); return; }
    setSaving(true);
    try {
      const eventData = { ...event, clientId: event.clientId || null, guestCount: event.guestCount ? parseInt(event.guestCount) : null, aiSuggestions: JSON.stringify(aiSuggestions) };
      let saved = eventId ? await api.put(`/events/${eventId}`, eventData) : await api.post('/events', eventData);
      
      await api.post(`/events/${saved.id}/quotes`, {
        lineItems: lineItems.map(li => ({ id: li.id, categoryId: li.categoryId || null, description: li.description, vendorId: li.vendorId || null, cost: li.cost || 0, price: li.price || 0, confirmed: li.confirmed || false }))
      });
      
      if (!eventId) navigate(`/admin/events/builder/${saved.id}`, { replace: true });
      setIsDirty(false);
      alert('Saved!');
    } catch (err) { alert('Save failed: ' + err.message); }
    finally { setSaving(false); }
  };

  // ==================== SEND QUOTE ====================
  
  const handleSend = () => {
    if (!event.name) { alert('Event name is required'); return; }
    if (!event.clientId) { alert('Please select a client'); return; }
    if (!lineItems.some(li => li.description)) { alert('Add at least one line item'); return; }
    
    const unconfirmed = lineItems.filter(li => !li.confirmed && li.description);
    if (unconfirmed.length > 0) {
      setShowSendWarning(true);
    } else {
      doSendQuote();
    }
  };
  
  const doSendQuote = async () => {
    setShowSendWarning(false);
    setSaving(true);
    try {
      const eventData = { ...event, clientId: event.clientId || null, guestCount: event.guestCount ? parseInt(event.guestCount) : null, aiSuggestions: JSON.stringify(aiSuggestions) };
      let saved = eventId ? await api.put(`/events/${eventId}`, eventData) : await api.post('/events', eventData);
      
      const quote = await api.post(`/events/${saved.id}/quotes`, {
        lineItems: lineItems.map(li => ({ id: li.id, categoryId: li.categoryId || null, description: li.description, vendorId: li.vendorId || null, cost: li.cost || 0, price: li.price || 0, confirmed: li.confirmed || false })),
        status: 'SENT'
      });
      
      if (event.status === 'INQUIRY') await api.put(`/events/${saved.id}`, { status: 'QUOTING' });

      // Generate PDF
      const client = clients.find(c => c.id === event.clientId);
      const validUntil = new Date(); validUntil.setDate(validUntil.getDate() + 30);
      
      const pdfData = {
        quoteNumber: quote.quoteNumber, validUntil: validUntil.toISOString(),
        event: { name: event.name, date: event.date, location: event.location, guestCount: event.guestCount, client: client || { name: 'TBD' } },
        client: client || { name: 'TBD' },
        lineItems: lineItems.filter(li => li.description).map(li => ({ description: li.description, price: li.price || 0 })),
        totalPrice, notes: event.notes
      };

      const res = await fetch(`${import.meta.env.VITE_API_URL || ''}/api/pdf/quote/preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${sessionStorage.getItem('token')}` },
        body: JSON.stringify(pdfData)
      });

      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = `Quote-${quote.quoteNumber || saved.id.slice(-8)}.pdf`;
        document.body.appendChild(a); a.click(); document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }

      if (!eventId) navigate(`/admin/events/builder/${saved.id}`, { replace: true });
      setAiSuggestions([]);
      setIsDirty(false);
      alert('Quote saved and downloaded!');
    } catch (err) { alert('Send failed: ' + err.message); }
    finally { setSaving(false); }
  };

  // Preview
  const handlePreview = async () => {
    const client = clients.find(c => c.id === event.clientId);
    const pdfData = {
      quoteNumber: 'PREVIEW',
      event: { name: event.name || 'Untitled', date: event.date, location: event.location, guestCount: event.guestCount, client: client || { name: 'TBD' } },
      client: client || { name: 'TBD' },
      lineItems: lineItems.filter(li => li.description).map(li => ({ description: li.description, price: li.price || 0 })),
      totalPrice, notes: event.notes
    };
    try {
      const res = await fetch(`${import.meta.env.VITE_API_URL || ''}/api/pdf/quote/preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${sessionStorage.getItem('token')}` },
        body: JSON.stringify(pdfData)
      });
      if (res.ok) { const blob = await res.blob(); window.open(URL.createObjectURL(blob), '_blank'); }
      else alert('Preview failed');
    } catch (err) { alert('Preview failed: ' + err.message); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-hub-600" /></div>;

  const selectedClient = clients.find(c => c.id === event.clientId);
  const hasCommittedRFQ = !!event.rfpText;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Event Builder™</h1>
          <p className="text-gray-500">Build and quote events</p>
        </div>
        <div className="flex gap-2">
          <button onClick={handleSave} disabled={saving} className="btn-secondary"><Save className="w-4 h-4" />Save Draft</button>
          <button onClick={handlePreview} className="btn-secondary"><Eye className="w-4 h-4" />Preview</button>
          <button onClick={handleSend} disabled={saving} className="btn-primary"><Send className="w-4 h-4" />Send Quote</button>
        </div>
      </div>

      {/* Summary Bar */}
      <div className="card p-4">
        <div className="grid grid-cols-2 sm:grid-cols-6 gap-4">
          <div><p className="text-xs text-gray-500">Client</p><p className="font-semibold">{selectedClient?.name || 'Not selected'}</p></div>
          <div><p className="text-xs text-gray-500">Event Date</p><p className="font-semibold">{event.date || '—'}</p></div>
          <div><p className="text-xs text-gray-500">Guests</p><p className="font-semibold">{event.guestCount || '—'}</p></div>
          <div><p className="text-xs text-gray-500">Revenue</p><p className="font-semibold text-green-600">{formatCurrency(totalPrice)}</p></div>
          <div><p className="text-xs text-gray-500">Cost</p><p className="font-semibold">{formatCurrency(totalCost)}</p></div>
          <div><p className="text-xs text-gray-500">Margin</p><p className="font-semibold text-green-600">{totalMargin}% ({formatCurrency(profit)})</p></div>
        </div>
      </div>

      {/* Brainstormer Section */}
      <div className="card">
        <div className="card-header flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-hub-600" />
          <h2 className="font-semibold">Event Brainstormer AI</h2>
        </div>
        <div className="card-body space-y-4">
          {/* Committed RFQ Display */}
          {hasCommittedRFQ && (
            <RFQDisplay rfpText={event.rfpText} parsedAt={event.rfpParsedAt} onEdit={editRFQ} />
          )}
          
          {/* Commit Banner (after parse, before commit) */}
          {pendingCommit && (
            <CommitBanner onCommit={commitRFQ} onClear={clearParse} />
          )}
          
          {/* Brainstorm Input (only if NO committed RFQ) */}
          {!hasCommittedRFQ && !pendingCommit && (
            <>
              <textarea
                value={brainstormText}
                onChange={(e) => setBrainstormText(e.target.value)}
                placeholder="Paste client RFP here, or describe the event needs..."
                className="input min-h-[120px]"
                rows={5}
              />
              <div className="flex justify-between items-center">
                <p className="text-xs text-gray-400">AI will parse your text, create line items, and suggest additional services.</p>
                <button onClick={handleBrainstorm} disabled={brainstormLoading || !brainstormText.trim()} className="btn-primary">
                  {brainstormLoading ? <><Loader2 className="w-4 h-4 animate-spin" />Parsing...</> : <><Sparkles className="w-4 h-4" />Parse & Generate</>}
                </button>
              </div>
            </>
          )}
          
          {/* AI Suggestions */}
          <AISuggestions suggestions={aiSuggestions} onAddToQuote={addSuggestionToQuote} onDelete={deleteSuggestion} onDismissAll={() => setAiSuggestions([])} />
          
          {/* Vendor Gaps Alert */}
          <VendorGapsAlert 
            gaps={vendorGaps} 
            onDiscover={(gap) => {
              // Open discovery modal with pre-filled search
              setShowDiscoveryModal({
                query: gap.searchQuery || gap.description || gap.categoryName,
                market: event.location || 'Las Vegas'
              });
            }}
            onDismiss={() => setVendorGaps([])}
          />
        </div>
      </div>

      {/* Event Details */}
      <div className="card">
        <div className="card-header"><h2 className="font-semibold">Event Details</h2></div>
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="label">Event Name *</label>
              <input type="text" value={event.name} onChange={(e) => handleEventChange('name', e.target.value)} className="input" placeholder="e.g. Acme Corp Annual Picnic" />
            </div>
            <div>
              <label className="label">Client</label>
              <div className="flex gap-2">
                <select value={event.clientId} onChange={(e) => handleEventChange('clientId', e.target.value)} className="input flex-1">
                  <option value="">Select client...</option>
                  {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                <button onClick={() => setShowClientModal(true)} className="btn-secondary px-3"><Plus className="w-4 h-4" /></button>
              </div>
            </div>
            <div>
              <label className="label">Event Date</label>
              <input type="date" value={event.date} onChange={(e) => handleEventChange('date', e.target.value)} className="input" />
            </div>
            <div>
              <label className="label">Guest Count</label>
              <input type="number" value={event.guestCount} onChange={(e) => handleEventChange('guestCount', e.target.value)} className="input" placeholder="350" min="0" />
            </div>
            <div className="md:col-span-2">
              <label className="label">Location / Venue</label>
              <input type="text" value={event.location} onChange={(e) => handleEventChange('location', e.target.value)} className="input" placeholder="e.g. Sunset Park, Las Vegas" />
            </div>
          </div>
        </div>
      </div>

      {/* Line Items */}
      <div className="card">
        <div className="card-header flex items-center justify-between">
          <h2 className="font-semibold">Line Items</h2>
          <button onClick={addLineItem} className="btn-secondary text-sm"><Plus className="w-4 h-4" />Add Line Item</button>
        </div>
        <div className="card-body space-y-3">
          {lineItems.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>No line items yet</p>
              <p className="text-sm mt-1">Use the AI Brainstormer above or add items manually</p>
            </div>
          ) : (
            lineItems.map((item, index) => (
              <LineItemRow
                key={index}
                item={item}
                index={index}
                categories={categories}
                vendors={vendors}
                onChange={handleLineItemChange}
                onDelete={deleteLineItem}
                onSearchVendors={searchVendorsForLine}
                onAddVendor={(idx, name) => setShowVendorModal({ index: idx, defaultName: name })}
                onDiscover={discoverVendors}
                vendorSearch={vendorSearches[index]}
              />
            ))
          )}

          {/* Totals */}
          {lineItems.length > 0 && (
            <div className="border-t pt-4 mt-4">
              <div className="flex justify-end">
                <div className="w-64 space-y-2">
                  <div className="flex justify-between text-sm"><span className="text-gray-600">Total Cost:</span><span className="font-medium">{formatCurrency(totalCost)}</span></div>
                  <div className="flex justify-between text-sm"><span className="text-gray-600">Total Price:</span><span className="font-semibold text-green-600">{formatCurrency(totalPrice)}</span></div>
                  <div className="flex justify-between text-sm border-t pt-2"><span className="text-gray-600">Gross Profit:</span><span className={`font-semibold ${profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>{formatCurrency(profit)} ({totalMargin}%)</span></div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      {showClientModal && <NewClientModal onSave={handleClientCreated} onClose={() => setShowClientModal(false)} />}
      {showVendorModal && <NewVendorModal onSave={handleVendorCreated} onClose={() => setShowVendorModal(null)} defaultMarket={event.location} defaultName={showVendorModal.defaultName || ''} />}
      {showSendWarning && <SendWarningModal unconfirmedItems={lineItems.filter(li => !li.confirmed && li.description)} onGoBack={() => setShowSendWarning(false)} onSendAnyway={doSendQuote} />}
      {showDiscoveryModal && (
        <VendorDiscoveryModal
          isOpen={true}
          onClose={() => setShowDiscoveryModal(null)}
          initialQuery={showDiscoveryModal.query}
          initialMarket={showDiscoveryModal.market}
          categories={categories}
          onVendorAdded={(vendor) => {
            // Add new vendor to the local list so it appears in dropdowns immediately
            setVendors(prev => [...prev, vendor]);
          }}
        />
      )}
    </div>
  );
}
